import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { updateInvoiceSettingApi } from "@/api/services/main/organization/invoice-setting";
import { IconSave } from "@/assets/icons";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/modules/irohana-components/elements/Button";
import { FooterActionBar } from "@/modules/irohana-components/elements/FooterActionBar";
import { Form } from "@/modules/irohana-components/elements/Form";
import { MenuNavigate } from "@/modules/irohana-components/elements/MenuNavigate";
import type { TMenuContent } from "@/modules/irohana-components/elements/MenuNavigate/MenuNavigate";
import { AppRoutes } from "@/routes/config";
import { InvoiceNavHeader } from "@/features/organization/invoice/components/InvoiceNavHeader";
import { BillingDueSection } from "@/features/organization/invoice/components/invoice-form/BillingDueSection";
import { BillingFlowSection } from "@/features/organization/invoice/components/invoice-form/BillingFlowSection";
import { BillingTreatmentSection } from "@/features/organization/invoice/components/invoice-form/BillingTreatmentSection";
import { DefaultAssigneeSection } from "@/features/organization/invoice/components/invoice-form/DefaultAssigneeSection";
import { FormatCodeSection } from "@/features/organization/invoice/components/invoice-form/FormatCodeSection";
import { TemplateSection } from "@/features/organization/invoice/components/invoice-form/TemplateSection";
import {
  DEFAULT_INVOICE_NUMBER_FORMAT,
  INVOICE_NUMBER_CODES,
  INVOICE_NUMBER_PREVIEW,
  INVOICE_SAVE_SUCCESS_MESSAGE,
} from "@/features/organization/invoice/constants/invoiceEnums";
import { invoiceSchema } from "@/features/organization/invoice/schema/invoiceSchema";
import { TInvoiceForm } from "@/features/organization/invoice/types/InvoiceForm";
import { convertFormToUpdateReq } from "@/features/organization/invoice/utils/invoiceConverts";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";

const invoiceNumberDescription = (
  <>
    請求書に記載する番号を設定できます。未設定の場合はデフォルトで「INV-&#123;年&#125;-&#123;連番/6&#125;」になります。 任意設定方法は
    <Box component="a" href="#" sx={{ color: text.link, textDecoration: "none" }} onClick={(e) => e.preventDefault()}>
      こちら
    </Box>
    からご確認ください。連番は必ず1つ必要です。
  </>
);

type TInvoiceSettingsFormProps = {
  defaultValues: TInvoiceForm;
};

/** 請求設定 (invoice settings) — edit form. Persists via PUT /organization/invoice-setting. */
export const InvoiceSettingsForm = ({ defaultValues }: TInvoiceSettingsFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [blockerEnabled, setBlockerEnabled] = useState(true);
  const [redirect, setRedirect] = useState<string | null>(null);

  const methods = useForm<TInvoiceForm>({
    defaultValues,
    resolver: yupResolver(invoiceSchema) as never,
    mode: "all",
    reValidateMode: "onBlur",
  });

  useEffect(() => {
    if (!blockerEnabled && redirect) {
      navigate(redirect);
      setRedirect(null);
    }
  }, [blockerEnabled, redirect, navigate]);

  const contents: TMenuContent[] = useMemo(
    () => [
      { title: "請求期日情報", content: <BillingDueSection /> },
      {
        title: "入退社月情報",
        content: (
          <BillingTreatmentSection
            title="入退社月情報"
            leftName="joinMonthTreatment"
            leftLabel="入社月"
            rightName="leaveMonthTreatment"
            rightLabel="退職月"
          />
        ),
      },
      {
        title: "一時帰国・休職情報",
        content: (
          <BillingTreatmentSection
            title="一時帰国・休職情報"
            leftName="temporaryReturnTreatment"
            leftLabel="一時帰国"
            rightName="leaveOfAbsenceTreatment"
            rightLabel="休職"
          />
        ),
      },
      {
        title: "請求書番号設定",
        content: (
          <FormatCodeSection
            title="請求書番号設定"
            description={invoiceNumberDescription}
            name="invoiceNumberFormat"
            codes={INVOICE_NUMBER_CODES}
            defaultFormat={DEFAULT_INVOICE_NUMBER_FORMAT}
            previewValue={INVOICE_NUMBER_PREVIEW}
          />
        ),
      },
      { title: "テンプレート設定", content: <TemplateSection /> },
      { title: "請求フロー設定", content: <BillingFlowSection /> },
      { title: "システム既定担当者", content: <DefaultAssigneeSection /> },
    ],
    [],
  );

  const onSubmit = async (data: TInvoiceForm) => {
    try {
      const res = await updateInvoiceSettingApi(convertFormToUpdateReq(data));
      toast.success(res.message ?? INVOICE_SAVE_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INVOICE_SETTING.FETCH_DETAIL] });
      methods.reset(data);
      setBlockerEnabled(false);
      setRedirect(AppRoutes.organizationInvoice);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={invoiceSchema} enableBlocker={blockerEnabled}>
      <InnerBox showDivider={false} sx={{ borderRadius: "0 0 8px 8px" }}>
        <MenuNavigate contents={contents} menuPrefix={<InvoiceNavHeader />} />
      </InnerBox>

      <Box mt={3}>
        <FooterActionBar>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button variant="ghost" onClick={() => navigate(AppRoutes.organizationInvoice)}>
              戻る
            </Button>
            <Button type="submit" variant="primary" size="sm" startIcon={<IconSave />} sx={{ fontWeight: 500 }}>
              保存
            </Button>
          </Stack>
        </FooterActionBar>
      </Box>
    </Form>
  );
};
